import { CellClassParams, ColDef, ValueFormatterParams } from '@ag-grid-community/core';

import { TableConstants } from '@shared/constants/table.constants';
import { Currency } from '@shared/services/gql.service';
import { decimalAdd } from '@shared/utils';
import { removeSymbolsFromString } from '@shared/utils/formatter.utils';
import { Utils } from '@shared/utils/utils';

import { InvoiceItemColumnInfo, InvoiceItemColumnType } from './invoice-items-via-pdf.model';

export const getValueFormatter = (columnType: InvoiceItemColumnType, currency: Currency) => {
  if (
    columnType === InvoiceItemColumnType.UNIT_PRICE ||
    columnType === InvoiceItemColumnType.PRICE
  ) {
    return {
      valueFormatter: (params: ValueFormatterParams) => {
        return Utils.agCurrencyFormatterAccounting(params, currency || Currency.USD, true);
      },
    };
  }

  return {
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.node?.rowPinned === 'bottom' && !params.value) {
        return Utils.zeroHyphen;
      }

      return;
    },
  };
};

export const currencyCodes = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'INR',
  'JPY',
  'CHF',
  'AUD',
  'CNY',
  'BZR',
  'SEK',
  'HKD',
];

export const parseColumnDefs = (
  columnDefs: string,
  currency: Currency
): (ColDef & { field: string })[] => {
  return JSON.parse(columnDefs)
    .sort((a: { field: string }, b: { field: string }) => a.field.localeCompare(b.field))
    .map(
      (column: InvoiceItemColumnInfo) =>
        <ColDef>{
          ...column,
          cellClass: (params: CellClassParams) => {
            if (
              params.colDef.type === InvoiceItemColumnType.UNIT_PRICE ||
              params.colDef.type === InvoiceItemColumnType.PRICE
            ) {
              return [`budgetCost${currency}`, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT];
            }

            if (params.colDef.type === InvoiceItemColumnType.QUANTITY) {
              return [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT];
            }

            return ['!block text-left max-w truncate'];
          },
          tooltipField: column.field,
          tooltipValueGetter: (params: { valueFormatted: string }) => {
            return params.valueFormatted !== Utils.zeroHyphen ? params.valueFormatted : '';
          },
          ...getValueFormatter(column.type, currency),
          resizable: true,
          sortable: false,
        }
    );
};

export const refactorToNumbers = (value: string): string => {
  const refactoredValue = removeSymbolsFromString(value, [...currencyCodes, '$', ' ', ',']);

  return Utils.isNumber(refactoredValue) ? refactoredValue : '';
};

export const calculatePinnedBottomData = (
  gridData: Record<
    string,
    {
      value: number | string;
    }
  >[],
  columnsToUpdate: string[]
) => {
  const obj = columnsToUpdate.reduce(
    (accum, value) => {
      return {
        ...accum,
        [value]: {
          value: 0,
        },
      };
    },
    {} as Record<
      string,
      {
        value: number;
      }
    >
  );

  return gridData.reduce((acc, value) => {
    for (const key of columnsToUpdate) {
      const currentVal = acc[key]?.value;
      const additionalVal = value[key]?.value;

      if (Utils.isNumber(currentVal) && Utils.isNumber(additionalVal)) {
        acc[key].value = decimalAdd(currentVal, additionalVal);
      }
    }

    return acc;
  }, obj);
};
